import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/utils/apiClient";
import { useAuth } from "@/state/auth";
import type {
  AudienceRole,
  ContentType,
  Locale,
  TemplateFull,
  TemplateStatus,
  TemplateSummary,
} from "@/types/content";

export interface ContentTemplateFilters {
  content_type?: ContentType;
  locale?: Locale;
  audience_role?: AudienceRole;
  status?: TemplateStatus;
}

export interface ContentTemplateListResponse {
  items: TemplateSummary[];
  total: number;
}

export interface CreateContentTemplatePayload {
  slug: string;
  content_type: ContentType;
  locale: Locale;
  audience_role: AudienceRole;
  title: string;
  description?: string | null;
  system_prompt: string;
  input_schema: TemplateFull["input_schema"];
  model_overrides?: TemplateFull["model_overrides"];
  use_rag: boolean;
  rag_context?: string | null;
  tts_voice?: string | null;
  status: TemplateStatus;
}

export interface UpdateContentTemplatePayload
  extends Partial<CreateContentTemplatePayload> {
  id: number;
}

const buildQuery = (filters?: ContentTemplateFilters): string => {
  if (!filters) return "";
  const params = new URLSearchParams();
  if (filters.content_type) params.set("content_type", filters.content_type);
  if (filters.locale) params.set("locale", filters.locale);
  if (filters.audience_role) params.set("audience_role", filters.audience_role);
  if (filters.status) params.set("status", filters.status);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

const fetchTemplates = async (
  filters?: ContentTemplateFilters
): Promise<ContentTemplateListResponse> => {
  return apiRequest<ContentTemplateListResponse>(
    `/api/admin/content-templates${buildQuery(filters)}`
  );
};

const fetchTemplate = async (
  id: number | "new"
): Promise<TemplateFull | null> => {
  if (id === "new") return null;
  const { template } = await apiRequest<{ template: TemplateFull }>(
    `/api/admin/content-templates/${id}`
  );
  return template;
};

const createTemplate = async (
  payload: CreateContentTemplatePayload
): Promise<TemplateFull> => {
  const { template } = await apiRequest<{ template: TemplateFull }>(
    "/api/admin/content-templates",
    { method: "POST", body: JSON.stringify(payload) }
  );
  return template;
};

const updateTemplate = async (
  payload: UpdateContentTemplatePayload
): Promise<TemplateFull> => {
  const { id, ...rest } = payload;
  const { template } = await apiRequest<{ template: TemplateFull }>(
    `/api/admin/content-templates/${id}`,
    { method: "PUT", body: JSON.stringify(rest) }
  );
  return template;
};

const archiveTemplate = async (id: number): Promise<void> => {
  await apiRequest<void>(`/api/admin/content-templates/${id}`, {
    method: "DELETE",
  });
};

export const useContentTemplates = (filters?: ContentTemplateFilters) => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: ["content-templates", "list", filters ?? {}],
    queryFn: () => fetchTemplates(filters),
    staleTime: 30_000,
    enabled: Boolean(profile),
  });
};

export const useContentTemplate = (id: number | "new") => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: ["content-templates", "detail", id],
    queryFn: () => fetchTemplate(id),
    enabled: Boolean(profile),
    staleTime: 0,
  });
};

export const useCreateContentTemplate = () => {
  const profile = useAuth((state) => state.profile);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateContentTemplatePayload) => createTemplate(payload),
    onSuccess: (template) => {
      if (!profile) return;
      queryClient.invalidateQueries({ queryKey: ["content-templates", "list"] });
      queryClient.setQueryData(
        ["content-templates", "detail", template.id],
        template
      );
    },
  });
};

export const useUpdateContentTemplate = () => {
  const profile = useAuth((state) => state.profile);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateContentTemplatePayload) => updateTemplate(payload),
    onSuccess: (template, variables) => {
      if (!profile) return;
      queryClient.invalidateQueries({ queryKey: ["content-templates", "list"] });
      queryClient.invalidateQueries({
        queryKey: ["content-templates", "detail", variables.id],
      });
      queryClient.setQueryData(
        ["content-templates", "detail", template.id],
        template
      );
    },
  });
};

export const useArchiveContentTemplate = () => {
  const profile = useAuth((state) => state.profile);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => archiveTemplate(id),
    onSuccess: (_void, id) => {
      if (!profile) return;
      queryClient.invalidateQueries({ queryKey: ["content-templates", "list"] });
      queryClient.invalidateQueries({
        queryKey: ["content-templates", "detail", id],
      });
    },
  });
};
